import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';

import useAccount from '@tcs/client-portal-common/hooks/useAccount';
import AccountInfoCard from '@tcs/client-portal-common/components/molecules/AccountInfoCard';
import AccountHoldingsCard from '@tcs/client-portal-common/components/molecules/AccountHoldingsCard';
import AccountHoldingsTable from '@tcs/client-portal-common/components/molecules/AccountHoldingsTable';
import Spinner from '@tcs/common/components/atoms/Spinner';
import AssetTransactionTable from '@tcs/client-portal-common/components/molecules/AssetActivityTable/AssetTransactionsTable';
import CashTransactionsTable from '@tcs/client-portal-common/components/molecules/AssetActivityTable/CashTransactionsTable';
import { Investment } from '@tcs/client-portal-api/types/Investment';
import { useStyles } from './AccountOverview.style';

interface Props {
  accountNumber: string;
}

const AccountOverview = ({ accountNumber }: Props) => {
  const { loading, data: account } = useAccount(accountNumber);
  const classes = useStyles();
  const [selectedAsset, setSelectedAsset] = useState<Investment | null>(null);
  const { assetId } = useParams();
  const nav = useNavigate();

  useEffect(() => {
    if (!assetId || !account) return;

    if (assetId === 'Cash') {
      setSelectedAsset(account.custodialCashBalance);
    } else {
      const asset = account.investmentBalances.find(
        (a) => a.assetCode === assetId
      );
      setSelectedAsset(asset);
    }
  }, [account, assetId]);

  if (loading) {
    return <Spinner />;
  }

  const handleBack = () => {
    nav('..');
    setSelectedAsset(null);
  };

  if (!account) {
    // @TODO: account not found. Show no data page
    return null;
  }

  const getTransactionTable = () => {
    if (selectedAsset.accountHoldingsType === 'Cash') {
      return (
        <CashTransactionsTable
          accountNumber={account.accountNumber}
          accountHolding={selectedAsset}
          onBackButtonClick={handleBack}
        />
      );
    }
    return (
      <AssetTransactionTable
        accountNumber={account.accountNumber}
        accountHolding={selectedAsset}
        onBackButtonClick={handleBack}
      />
    );
  };

  return (
    <div>
      <div className={classes.wrapper}>
        <AccountInfoCard title="Total Value of Account" {...account} />
        <AccountHoldingsCard
          title="Investment allocation"
          cashBalance={account.custodialCashBalance}
          investmentBalances={account.investmentBalances}
          {...account}
        />
      </div>
      {selectedAsset ? (
        getTransactionTable()
      ) : (
        <AccountHoldingsTable
          cashBalance={account.custodialCashBalance}
          investmentBalances={account.investmentBalances}
          onRowClick={setSelectedAsset}
        />
      )}
    </div>
  );
};

export default AccountOverview;
